import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Check, X, Clock, RefreshCw, ArrowRight, Inbox, CalendarPlus } from 'lucide-react';
import { toast } from 'sonner';
import { madridTime } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return madridTime(iso, '—');
  } catch { return iso; }
};

const STATUS_BADGE = {
  pending: { label: 'Pendiente', cls: 'bg-amber-100 text-amber-900 border-amber-300' },
  approved: { label: 'Aprobada', cls: 'bg-green-100 text-green-900 border-green-300' },
  rejected: { label: 'Rechazada', cls: 'bg-red-100 text-red-900 border-red-300' },
};

const CorrectionRequestsPanel = () => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [reviewNotes, setReviewNotes] = useState({});

  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === 'all'
        ? `${API_URL}/api/timeclock/correction-requests`
        : `${API_URL}/api/timeclock/correction-requests?status=${filter}`;
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error('Error');
      setRequests(await res.json());
    } catch (e) {
      toast.error('No se pudieron cargar las solicitudes');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleAction = async (reqId, action) => {
    const note = reviewNotes[reqId] || '';
    try {
      const res = await fetch(`${API_URL}/api/timeclock/correction-requests/${reqId}/${action}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ review_note: note }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error');
      }
      toast.success(action === 'approve' ? 'Corrección aprobada' : 'Solicitud rechazada');
      setReviewNotes(prev => { const n = { ...prev }; delete n[reqId]; return n; });
      fetchRequests();
    } catch (e) {
      toast.error(`No se pudo ${action === 'approve' ? 'aprobar' : 'rechazar'}: ${e.message}`);
    }
  };

  return (
    <div className="space-y-4" data-testid="correction-requests-panel">
      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {['pending', 'approved', 'rejected', 'all'].map(s => (
          <Button
            key={s}
            data-testid={`corr-filter-${s}`}
            size="sm"
            variant={filter === s ? 'default' : 'outline'}
            onClick={() => setFilter(s)}
          >
            {s === 'all' ? 'Todas' : STATUS_BADGE[s].label}
          </Button>
        ))}
        <Button size="sm" variant="ghost" onClick={fetchRequests} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Empty state */}
      {!loading && requests.length === 0 && (
        <Card data-testid="corr-empty-state">
          <CardContent className="p-10 flex flex-col items-center justify-center text-stone-500">
            <Inbox className="w-10 h-10 mb-3" />
            <p className="text-sm">No hay solicitudes en este estado.</p>
          </CardContent>
        </Card>
      )}

      {/* Request list */}
      <div className="space-y-3">
        {requests.map(req => {
          const isPending = req.status === 'pending';
          const sb = STATUS_BADGE[req.status] || STATUS_BADGE.pending;
          return (
            <Card key={req.id} data-testid={`corr-req-${req.id}`}>
              <CardContent className="p-5">
                <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-base">{req.employee_name || '—'}</p>
                      {req.missed_day && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300"
                          data-testid={`corr-missed-badge-${req.id}`}
                        >
                          <CalendarPlus className="w-3 h-3" /> Día olvidado
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-stone-500">
                      {req.missed_day && req.date && <>Para el día <strong>{req.date}</strong> · </>}
                      Solicitada {fmtDate(req.created_at)}
                    </p>
                  </div>
                  <Badge className={`border ${sb.cls}`}>{sb.label}</Badge>
                </div>

                {/* Before / After (or just "New" when missed_day) */}
                {req.missed_day ? (
                  <div className="bg-amber-50 rounded-lg p-3 border border-amber-200 mb-3">
                    <p className="text-[11px] uppercase tracking-wider text-amber-800 mb-1.5 flex items-center gap-1">
                      <ArrowRight className="w-3 h-3" /> Horario propuesto a crear
                    </p>
                    <div className="flex gap-3 text-sm">
                      <span>Entrada: <strong>{fmtTime(req.proposed_clock_in)}</strong></span>
                      <span>Salida: <strong>{fmtTime(req.proposed_clock_out)}</strong></span>
                    </div>
                    <p className="text-[11px] text-amber-700 mt-1">No había fichaje ese día. Aprobar creará el registro desde cero.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div className="bg-stone-50 rounded-lg p-3">
                      <p className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">Actual</p>
                      <div className="flex gap-3 text-sm">
                        <span>Entrada: <strong>{fmtTime(req.current_clock_in)}</strong></span>
                        <span>Salida: <strong>{fmtTime(req.current_clock_out)}</strong></span>
                      </div>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                      <p className="text-[11px] uppercase tracking-wider text-amber-800 mb-1.5 flex items-center gap-1">
                        <ArrowRight className="w-3 h-3" /> Propuesta
                      </p>
                      <div className="flex gap-3 text-sm">
                        <span>Entrada: <strong>{fmtTime(req.proposed_clock_in) || fmtTime(req.current_clock_in)}</strong></span>
                        <span>Salida: <strong>{fmtTime(req.proposed_clock_out) || fmtTime(req.current_clock_out)}</strong></span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reason */}
                <div className="mb-3">
                  <p className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Motivo</p>
                  <p className="text-sm text-stone-700">{req.reason}</p>
                </div>

                {/* Reviewer note (if reviewed) */}
                {!isPending && (req.reviewed_at || req.review_note) && (
                  <div className="mb-3 text-sm text-stone-600 border-t pt-3">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Revisada {fmtDate(req.reviewed_at)} por <strong>{req.reviewed_by || '—'}</strong>
                    {req.review_note && <p className="mt-1 italic">"{req.review_note}"</p>}
                  </div>
                )}

                {/* Actions (only for pending) */}
                {isPending && (
                  <div className="space-y-2 border-t pt-3">
                    <Textarea
                      data-testid={`corr-note-${req.id}`}
                      placeholder="Nota interna (opcional)"
                      value={reviewNotes[req.id] || ''}
                      onChange={(e) => setReviewNotes(prev => ({ ...prev, [req.id]: e.target.value }))}
                      rows={2}
                      className="text-sm"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        data-testid={`corr-approve-${req.id}`}
                        onClick={() => handleAction(req.id, 'approve')}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Check className="w-4 h-4 mr-1" /> Aprobar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`corr-reject-${req.id}`}
                        onClick={() => handleAction(req.id, 'reject')}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <X className="w-4 h-4 mr-1" /> Rechazar
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default CorrectionRequestsPanel;
